import {captureSelection,restoreSelection} from '../utils/selection';
import { useCallback, useEffect, useRef, useState } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $isElementNode,
  $isTextNode,
  $createParagraphNode,
  UNDO_COMMAND,
  REDO_COMMAND,
  CAN_UNDO_COMMAND,
  CAN_REDO_COMMAND,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  INDENT_CONTENT_COMMAND,
  OUTDENT_CONTENT_COMMAND,
} from "lexical";
import {
  $setBlocksType,
  $patchStyleText,
  $getSelectionStyleValueForProperty,
} from "@lexical/selection";
import { $createHeadingNode, $isHeadingNode } from "@lexical/rich-text";
import {
  ListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import {
  INSERT_TABLE_COMMAND,
  $isTableNode,
  $insertTableRowAtSelection,
  $insertTableColumnAtSelection,
  $deleteTableRowAtSelection,
  $deleteTableColumnAtSelection,
} from "@lexical/table";
import { INSERT_HORIZONTAL_RULE_COMMAND } from "@lexical/react/LexicalHorizontalRuleNode";
import { $findMatchingParent, $getNearestNodeOfType } from "@lexical/utils";
import { $createImageNode } from "../nodes/ImageNode.jsx";
import { $createBlockTokenNode } from "../nodes/BlockTokenNode.jsx";
import { $isPageNode } from "../nodes/PageNode.jsx";
import {
  $addPage,
  $movePage,
  $deletePage,
  $duplicatePage,
  pageCount,
} from "../utils/pagesCommands";
import {
  FONT_FAMILIES,
  FONT_SIZES,
  DEFAULT_FONT,
  DEFAULT_SIZE,
  cssFamily,
  normFamily,
} from "../constants";

const closeMenu = (e) =>
  e.currentTarget.closest("details")?.removeAttribute("open");

function Btn({ title, active, disabled, onClick, children }) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      className={`de-btn${active ? " is-active" : ""}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export default function ToolbarPlugin({
  fields,
  dirty,
  saving,
  disabled,
  onSave,
  onPreview,
  onFind,
  onHelp,
  onUpload,
  onDownload,
}) {
  const [editor] = useLexicalComposerContext();
  const [fmt, setFmt] = useState({
    bold: false,
    italic: false,
    underline: false,
  });
  const [block, setBlock] = useState("paragraph");
  const [listType, setListType] = useState("");
  const [inTable, setInTable] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);
  const [fontFamily, setFontFamily] = useState(DEFAULT_FONT);
  const [fontSize, setFontSize] = useState(DEFAULT_SIZE);
  const fileRef = useRef(null);
  const selectionRef = useRef(null);

  const readState = useCallback(() => {
    const sel = $getSelection();
    if (!$isRangeSelection(sel)) return;
    selectionRef.current = sel.clone();
    setFmt({
      bold: sel.hasFormat("bold"),
      italic: sel.hasFormat("italic"),
      underline: sel.hasFormat("underline"),
    });
    const anchor = sel.anchor.getNode();
    const blk = $findMatchingParent(
      anchor,
      (n) => $isElementNode(n) && !n.isInline(),
    );
    setBlock($isHeadingNode(blk) ? blk.getTag() : "paragraph");
    const list = $getNearestNodeOfType(anchor, ListNode);
    setListType(list ? list.getListType() : "");
    setInTable(!!$findMatchingParent(anchor, $isTableNode));
    setFontFamily(
      normFamily(
        $getSelectionStyleValueForProperty(sel, "font-family", DEFAULT_FONT),
      ) || DEFAULT_FONT,
    );
    const sz = parseFloat(
      $getSelectionStyleValueForProperty(sel, "font-size", `${DEFAULT_SIZE}pt`),
    );
    setFontSize(Number.isFinite(sz) ? sz : DEFAULT_SIZE);
  }, []);

  useEffect(() => {
    const offs = [
      editor.registerUpdateListener(({ editorState }) =>
        editorState.read(readState),
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (v) => {
          setCanUndo(v);
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (v) => {
          setCanRedo(v);
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    ];
    return () => offs.forEach((o) => o());
  }, [editor, readState]);

  const run = (fn) => {
    editor.update(() => { restoreSelection(selectionRef.current); fn(); });
    editor.focus();
  };
  const cmd = (c, p) => {
    editor.update(() => { restoreSelection(selectionRef.current); editor.dispatchCommand(c, p); });
    editor.focus();
  };

  const setBlockType = (v) =>
    run(() => {
      const sel = $getSelection();
      if ($isRangeSelection(sel)) {
        $setBlocksType(sel, () =>
          v === "paragraph" ? $createParagraphNode() : $createHeadingNode(v),
        );
      }
    });

  const applyFont = (prop, value) =>
    run(() => {
      const sel = $getSelection();
      if ($isRangeSelection(sel)) $patchStyleText(sel, { [prop]: value });
    });

  const toggleList = (type) => {
    if (listType === type) cmd(REMOVE_LIST_COMMAND);
    else
      cmd(
        type === "bullet"
          ? INSERT_UNORDERED_LIST_COMMAND
          : INSERT_ORDERED_LIST_COMMAND,
      );
  };

  const clearFormatting = () =>
    run(() => {
      const sel = $getSelection();
      if (!$isRangeSelection(sel)) return;
      sel.getNodes().forEach((n) => {
        if ($isTextNode(n)) {
          n.setFormat(0);
          n.setStyle("");
        }
      });
      if (!listType && !inTable)
        $setBlocksType(sel, () => $createParagraphNode());
    });

  const insertField = (key) => {
    if (!key) return;
    run(() => {
      let sel = $getSelection();
      if (!$isRangeSelection(sel)) {
        // The user has not clicked in the document yet: insert at the end of it.
        $getRoot().selectEnd();
        sel = $getSelection();
        if (!$isRangeSelection(sel)) return;
      }
      if (key === "room_details" || key === "seal") {
        const top = $findMatchingParent(
          sel.anchor.getNode(),
          (n) => $isElementNode(n) && $isPageNode(n.getParent()),
        );
        if (!top) return;
        const node = $createBlockTokenNode(key);
        top.insertAfter(node);
        if (!node.getNextSibling()) node.insertAfter($createParagraphNode());
        return;
      }
      sel.insertText(`{{${key}}}`);
    });
  };

  const onImage = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file || !/^image\/(png|jpe?g|gif|webp)$/.test(file.type)) return;
    const reader = new FileReader();
    reader.onload = () =>
      run(() => {
        const sel = $getSelection();
        if ($isRangeSelection(sel))
          sel.insertNodes([$createImageNode(String(reader.result))]);
      });
    reader.readAsDataURL(file);
  };

  const deleteTable = () =>
    run(() => {
      const sel = $getSelection();
      if (!$isRangeSelection(sel)) return;
      const t = $findMatchingParent(sel.anchor.getNode(), $isTableNode);
      if (t) t.remove();
    });

  const deletePage = () => {
    let count = 1;
    editor.getEditorState().read(() => {
      count = pageCount();
    });
    if (count <= 1) {
      window.alert("A document needs at least one page.");
      return;
    }
    if (window.confirm("Delete this page and its content?"))
      run(() => $deletePage());
  };

  const off = disabled;
  return (
    <div
      className="de-toolbar"
      onMouseDown={(e) => {
        selectionRef.current = captureSelection(editor) || selectionRef.current;
        if (!e.target.closest("input, select")) e.preventDefault();
      }}
    >
      <select
        className="de-keys"
        disabled={off}
        value=""
        onChange={(e) => insertField(e.target.value)}
        aria-label="Keys"
        title="Insert a key (field) into the document"
      >
        <option value="" disabled>
          Keys
        </option>
        {fields.map((f) => (
          <option key={f.key} value={f.key}>
            {`${f.label}  {{${f.key}}}`}
          </option>
        ))}
        <option value="seal">{"Seal block  {{seal}}"}</option>
      </select>
      <span className="de-sep" />
      <Btn
        title="Undo (Ctrl+Z)"
        disabled={off || !canUndo}
        onClick={() => cmd(UNDO_COMMAND)}
      >
        Undo
      </Btn>
      <Btn
        title="Redo (Ctrl+Y)"
        disabled={off || !canRedo}
        onClick={() => cmd(REDO_COMMAND)}
      >
        Redo
      </Btn>
      <span className="de-sep" />
      <select
        disabled={off}
        value={block}
        onChange={(e) => setBlockType(e.target.value)}
        aria-label="Block type"
      >
        <option value="paragraph">Paragraph</option>
        <option value="h1">Heading 1</option>
        <option value="h2">Heading 2</option>
        <option value="h3">Heading 3</option>
      </select>
      <select
        disabled={off}
        value={fontFamily}
        onChange={(e) => applyFont("font-family", cssFamily(e.target.value))}
        aria-label="Font"
      >
        {FONT_FAMILIES.map((f) => (
          <option key={f} value={f}>
            {f}
          </option>
        ))}
      </select>
      <select
        disabled={off}
        value={fontSize}
        onChange={(e) => applyFont("font-size", `${e.target.value}pt`)}
        aria-label="Font size"
      >
        {FONT_SIZES.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
      <Btn
        title="Bold (Ctrl+B)"
        active={fmt.bold}
        disabled={off}
        onClick={() => cmd(FORMAT_TEXT_COMMAND, "bold")}
      >
        <b>B</b>
      </Btn>
      <Btn
        title="Italic (Ctrl+I)"
        active={fmt.italic}
        disabled={off}
        onClick={() => cmd(FORMAT_TEXT_COMMAND, "italic")}
      >
        <i>I</i>
      </Btn>
      <Btn
        title="Underline (Ctrl+U)"
        active={fmt.underline}
        disabled={off}
        onClick={() => cmd(FORMAT_TEXT_COMMAND, "underline")}
      >
        <u>U</u>
      </Btn>
      <span className="de-sep" />
      <Btn
        title="Bullet list"
        active={listType === "bullet"}
        disabled={off}
        onClick={() => toggleList("bullet")}
      >
        &bull; List
      </Btn>
      <Btn
        title="Numbered list"
        active={listType === "number"}
        disabled={off}
        onClick={() => toggleList("number")}
      >
        1. List
      </Btn>
      <Btn
        title="Indent"
        disabled={off}
        onClick={() => cmd(INDENT_CONTENT_COMMAND)}
      >
        Indent
      </Btn>
      <Btn
        title="Outdent"
        disabled={off}
        onClick={() => cmd(OUTDENT_CONTENT_COMMAND)}
      >
        Outdent
      </Btn>
      <span className="de-sep" />
      <Btn
        title="Align left"
        disabled={off}
        onClick={() => cmd(FORMAT_ELEMENT_COMMAND, "left")}
      >
        Left
      </Btn>
      <Btn
        title="Align center"
        disabled={off}
        onClick={() => cmd(FORMAT_ELEMENT_COMMAND, "center")}
      >
        Center
      </Btn>
      <Btn
        title="Align right"
        disabled={off}
        onClick={() => cmd(FORMAT_ELEMENT_COMMAND, "right")}
      >
        Right
      </Btn>
      <Btn
        title="Horizontal rule"
        disabled={off}
        onClick={() => cmd(INSERT_HORIZONTAL_RULE_COMMAND)}
      >
        HR
      </Btn>
      <Btn title="Clear formatting" disabled={off} onClick={clearFormatting}>
        Clear
      </Btn>
      <span className="de-sep" />

      <details className="de-menu">
        <summary className={off ? "is-disabled" : ""}>Table</summary>
        <div className="de-menu-body">
          <label>
            Rows{" "}
            <input
              type="number"
              min="1"
              max="30"
              value={rows}
              onChange={(e) => setRows(+e.target.value || 1)}
            />
          </label>
          <label>
            Cols{" "}
            <input
              type="number"
              min="1"
              max="12"
              value={cols}
              onChange={(e) => setCols(+e.target.value || 1)}
            />
          </label>
          <button
            type="button"
            onClick={(e) => {
              cmd(INSERT_TABLE_COMMAND, {
                rows: String(rows),
                columns: String(cols),
                includeHeaders: false,
              });
              closeMenu(e);
            }}
          >
            Insert table
          </button>
          <hr />
          <button
            type="button"
            disabled={!inTable}
            onClick={() => run(() => $insertTableRowAtSelection(true))}
          >
            Add row below
          </button>
          <button
            type="button"
            disabled={!inTable}
            onClick={() => run(() => $insertTableColumnAtSelection(true))}
          >
            Add column right
          </button>
          <button
            type="button"
            disabled={!inTable}
            onClick={() => run(() => $deleteTableRowAtSelection())}
          >
            Delete row
          </button>
          <button
            type="button"
            disabled={!inTable}
            onClick={() => run(() => $deleteTableColumnAtSelection())}
          >
            Delete column
          </button>
          <button
            type="button"
            disabled={!inTable}
            onClick={(e) => {
              deleteTable();
              closeMenu(e);
            }}
          >
            Delete table
          </button>
        </div>
      </details>

      <details className="de-menu">
        <summary className={off ? "is-disabled" : ""}>Pages</summary>
        <div className="de-menu-body">
          <button
            type="button"
            onClick={(e) => {
              run(() => $addPage());
              closeMenu(e);
            }}
          >
            Add page after
          </button>
          <button
            type="button"
            onClick={(e) => {
              run(() => $duplicatePage(editor));
              closeMenu(e);
            }}
          >
            Duplicate page
          </button>
          <button
            type="button"
            onClick={(e) => {
              run(() => $movePage(-1));
              closeMenu(e);
            }}
          >
            Move up
          </button>
          <button
            type="button"
            onClick={(e) => {
              run(() => $movePage(1));
              closeMenu(e);
            }}
          >
            Move down
          </button>
          <button
            type="button"
            onClick={(e) => {
              deletePage();
              closeMenu(e);
            }}
          >
            Delete page
          </button>
        </div>
      </details>

      <Btn
        title="Insert image"
        disabled={off}
        onClick={() => fileRef.current && fileRef.current.click()}
      >
        Image
      </Btn>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        hidden
        onChange={onImage}
      />

      <Btn
        title="Find & Replace (Ctrl+F / Ctrl+H)"
        disabled={off}
        onClick={onFind}
      >
        Find
      </Btn>
      <Btn title="Preview" disabled={off} onClick={onPreview}>
        Preview
      </Btn>
      <Btn title="Upload Word (.docx)" disabled={off} onClick={() => onUpload(selectionRef.current)}>Upload Word</Btn>
      <Btn title="Download PDF, HTML or Word" disabled={off} onClick={onDownload}>Download</Btn>
      <Btn title="How to use (F1)" onClick={onHelp}>How to use</Btn>
      <span className="de-spacer" />
      {dirty && (
        <span className="de-dirty" title="Unsaved changes">
          &#9679; Unsaved
        </span>
      )}
      <button
        type="button"
        className="de-save"
        disabled={off || saving}
        onClick={onSave}
        title="Save (Ctrl+S)"
      >
        {saving ? "Saving..." : "Save"}
      </button>
    </div>
  );
}